import { cn } from '@exam-room/utils';
import { useWatch, useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { ExamOverviewItem } from '../../components';
import { useNewExam, useNewExamFormOptional } from '../../providers';
import { useExamPreviewFormOptional } from '../../providers/exam-preview';

interface ExamOverviewList extends React.ComponentProps<'div'> {
  minified?: boolean;
  onTileClick?: () => void;
}

export default function ExamOverviewList({
  minified,
  className,
  onTileClick,
  ...props
}: ExamOverviewList) {
  const [searchParams, setSearchParams] = useSearchParams({});
  const questionId = searchParams.get('q');
  const page = searchParams.get('page');

  const { questions } = useNewExam();

  const formContext = useFormContext();
  const newExamFormContext = useNewExamFormOptional();
  const previewFormContext = useExamPreviewFormOptional();

  const submitContext = newExamFormContext || previewFormContext;

  if (!submitContext) {
    throw new Error(
      'ExamOverviewList must be used within either NewExamFormProvider or ExamPreviewFormProvider',
    );
  }

  const { getValues } = formContext;
  const answer = getValues();

  // For SET eExam - Watch all bookmark values to trigger rerender when they change - to immediately show the bookmark icon
  useWatch({
    name: questions?.map((q) => `${q.id}.isBookmark`) ?? [],
  });

  const exampleQuestionList = Array.isArray(questions) ? questions : [];

  return (
    <div
      className={cn(
        minified ? 'flex flex-wrap gap-1 pt-1' : 'grid grid-cols-10 gap-2',
        className,
        'max-h-full overflow-x-hidden overflow-y-scroll',
      )}
      {...props}
    >
      {(minified ? questions : exampleQuestionList)?.map((question) => (
        <ExamOverviewItem
          key={question.id}
          size={minified ? 'sm' : 'md'}
          current={page === 'exam' ? question.id === questionId : false}
          bookmarked={answer[question.id]?.isBookmark}
          done={answer[question.id]?.answer}
          onClick={() => {
            const params = new URLSearchParams();
            params.set('q', question.id);
            params.set('page', 'exam');

            const query = params.toString();
            setSearchParams(query);
            onTileClick?.();
          }}
        >
          {question.order}
        </ExamOverviewItem>
      ))}
    </div>
  );
}
